import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import { formatCurrency, useTariff, monthlyPerformanceService, vortxColors, vortxTypography, TARIFF_DEFAULTS } from '@vortx/shared-ui';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

function formatPercentage(value) {
  if (typeof value !== 'number' || isNaN(value)) return '-';
  return `${value.toFixed(1)}%`;
}

function formatEnergy(kwh) {
  if (typeof kwh !== 'number' || isNaN(kwh)) return '-';
  return kwh.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatPower(kw) {
  if (typeof kw !== 'number' || isNaN(kw)) return '-';
  return `${kw.toFixed(1)} kW`;
}

export default function MonthlyPerformanceTable({ runUuid, results, tariff: propTariff, caDerConfig, monthlyPerformanceData: propData, utilityCostsOnly = false }) {
  // SINGLE SOURCE OF TRUTH: Use data passed from ScenarioEditor if available
  // Only fetch independently if no data is passed (backwards compatibility)
  const [fetchedData, setFetchedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = Performance, 1 = Savings
  const [savingsSubTab, setSavingsSubTab] = useState(0); // 0 = Utility, 1 = DER Charges, 2 = Summary
  const { selectedTariff: contextTariff } = useTariff();
  // Prefer prop tariff over context tariff (prop is more reliable from ScenarioEditor)
  const selectedTariff = propTariff || contextTariff;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Use prop data if available, otherwise use fetched data
  const monthlyData = propData || fetchedData;

  // Only fetch if no data is passed from parent (backwards compatibility)
  useEffect(() => {
    // If data is passed from parent, don't fetch independently
    if (propData) {
      console.log('MonthlyPerformanceTable: Using data from parent (single source of truth)');
      return;
    }

    if (!results) {
      setFetchedData(null);
      return;
    }

    const fetchMonthlyData = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await monthlyPerformanceService.fetchMonthlyPerformance(results, selectedTariff, { caDerConfig });
        console.log('MonthlyPerformanceTable fetched data (no prop data):', data);
        setFetchedData(data);
      } catch (err) {
        console.error('Failed to fetch monthly performance data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyData();
  }, [runUuid, results, selectedTariff, caDerConfig, propData]);

  if (!results) {
    return null;
  }

  if (loading) {
    return (
      <Card sx={{ mt: 3, bgcolor: 'background.paper', color: 'text.primary' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} sx={{ color: vortxColors.gold }} />
            <Typography sx={{ fontFamily: vortxTypography.body, color: 'text.primary' }}>Loading monthly performance data...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mt: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          <Alert severity="warning" sx={{ bgcolor: isDark ? 'rgba(237, 108, 2, 0.15)' : 'rgba(237, 108, 2, 0.15)', color: isDark ? '#ff9800' : vortxColors.charcoal, '& .MuiAlert-icon': { color: '#ed6c02' } }}>
            <Typography sx={{ fontFamily: vortxTypography.body }}>Failed to load monthly performance data: {error}</Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!monthlyData) {
    return null;
  }

  // Handle both old structure (asset_performance) and new structure (monthly_performance.monthly_performance)
  const asset_performance = monthlyData.asset_performance ||
    monthlyData.monthly_performance?.monthly_performance ||
    [];
  const bau_monthly_costs = monthlyData.bau_monthly_costs || [];
  const project_monthly_costs = monthlyData.project_monthly_costs || [];
  const monthly_savings = monthlyData.monthly_savings || [];
  const monthly_der_charges = monthlyData.monthly_der_charges || [];

  // Debug logging for savings data
  console.log('MonthlyPerformanceTable - bau_monthly_costs:', bau_monthly_costs);
  console.log('MonthlyPerformanceTable - project_monthly_costs:', project_monthly_costs);
  console.log('MonthlyPerformanceTable - monthly_savings:', monthly_savings);

  // Calculate column visibility based on data presence
  const hasStorage = asset_performance.some(m => (m.storage_to_load_kwh || 0) > 1); // Threshold 1 kWh to avoid noise
  const hasGenerator = asset_performance.some(m => (m.generator_to_load_kwh || 0) > 1);
  const hasSolar = asset_performance.some(m => (m.pv_production_kwh || m.pv_to_load_kwh || 0) > 1);
  const hasDerCharges = monthly_der_charges.length > 0 && monthly_der_charges.some(m => (m.total_der_charges || 0) > 0);
  const hasStandbyCharges = monthly_der_charges.length > 0 && monthly_der_charges.some(m => (m.standby_cost || 0) > 0);
  const hasDepartingLoadCharges = monthly_der_charges.length > 0 && monthly_der_charges.some(m => (m.departing_load_cost || 0) > 0);

  // Check if fuel cost breakdown is available (delivery/commodity split from gas tariff)
  const hasFuelCostBreakdown = hasGenerator && asset_performance.some(m =>
    (m.generator_fuel_delivery_cost !== undefined && m.generator_fuel_delivery_cost !== null) ||
    (m.generator_fuel_commodity_cost !== undefined && m.generator_fuel_commodity_cost !== null)
  );

  return (
    <Card sx={{ mt: 3, bgcolor: 'background.paper', color: 'text.primary' }}>
      <CardContent>
        {/* Show data source warning for estimates */}
        {monthlyData?.tariff_data_source === 'estimated' && (
          <Alert severity="warning" sx={{ mb: 2, bgcolor: 'rgba(237, 108, 2, 0.15)', color: theme.palette.warning.main, '& .MuiAlert-icon': { color: theme.palette.warning.main } }}>
            <Typography variant="body2" sx={{ color: 'inherit' }}>
              <strong>Cost calculations are estimated</strong> - Using approximate rates based on tariff type.
              Actual tariff rate structure data was not available for detailed calculations.
            </Typography>
          </Alert>
        )}

        {/* Show warning when using generic fallback tariff */}
        {/* Blocking Error for Missing Tariff */}
        {monthlyData?.tariff_data_source === 'missing' && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: isDark ? 'rgba(211, 47, 47, 0.15)' : 'rgba(211, 47, 47, 0.1)', color: theme.palette.error.main, '& .MuiAlert-icon': { color: theme.palette.error.main } }}>
            <Typography variant="body2" sx={{ color: 'inherit' }}>
              <strong>⛔ No Tariff Selected</strong>
              <br />
              Financial analysis cannot be performed without a utility tariff.
              <br />
              Please go to the <strong>Utility</strong> tab in the Scenario Editor and select a tariff.
            </Typography>
          </Alert>
        )}

        {monthlyData?.tariff_data_source === 'urdb_actual' && (
          <Alert severity="success" sx={{ mb: 2, bgcolor: 'rgba(46, 125, 50, 0.15)', color: theme.palette.success.main, '& .MuiAlert-icon': { color: theme.palette.success.main } }}>
            <Typography variant="body2" sx={{ color: 'inherit' }}>
              <strong>Using actual tariff rate data</strong> - Cost calculations based on complete utility rate structure.
            </Typography>
          </Alert>
        )}

        {monthlyData?.tariff_data_source !== 'missing' && (
          <>
            {/* Sub-tabs for Performance and Savings */}
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                mb: 2,
                '& .MuiTab-root': {
                  fontFamily: vortxTypography.body,
                  fontWeight: 600,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: vortxColors.gold,
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: vortxColors.gold,
                }
              }}
            >
              <Tab label="Performance" />
              <Tab label="Savings" />
            </Tabs>

            {/* Performance Tab */}
            {activeTab === 0 && (
              <TableContainer
                component={Paper}
                sx={{
                  mt: 2,
                  bgcolor: 'background.paper',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(63, 106, 100, 0.2)'}`,
                  '@media (min-width: 1200px)': {
                    maxHeight: 'none',
                  },
                  '@media (max-width: 1199px)': {
                    maxHeight: 600,
                  }
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 60, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>Month</TableCell>
                      <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 80, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>
                        Load<br />
                        <Typography variant="caption" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>kWh</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 80, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>
                        Grid Import<br />
                        <Typography variant="caption" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>kWh</Typography>
                      </TableCell>
                      {hasSolar && (
                        <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 80, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>
                          Solar Generation<br />
                          <Typography variant="caption" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>kWh</Typography>
                        </TableCell>
                      )}
                      {hasSolar && (
                        <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 100, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>
                          Energy Export<br />
                          <Typography variant="caption" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}>Solar / BESS kWh</Typography>
                        </TableCell>
                      )}
                      {hasStorage && (
                        <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 80, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>
                          BESS<br />
                          <Typography variant="caption" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>Throughput</Typography>
                        </TableCell>
                      )}
                      {hasStorage && (
                        <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 70, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>
                          Battery<br />
                          <Typography variant="caption" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>Cycles</Typography>
                        </TableCell>
                      )}
                      {hasGenerator && (
                        <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 90, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>
                          Generator<br />
                          <Typography variant="caption" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>kWh</Typography>
                        </TableCell>
                      )}
                      {hasGenerator && (
                        <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 90, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>
                          Run Hours<br />
                          <Typography variant="caption" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>Gen</Typography>
                        </TableCell>
                      )}
                      {hasGenerator && (
                        <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 90, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>
                          Fuel Used<br />
                          <Typography variant="caption" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>MMBtu</Typography>
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 120, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.9rem' }}>
                        Peak Demand<br />
                        <Typography variant="caption" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}>Pre / Post kW</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {MONTH_NAMES.map((monthName, index) => {
                      const asset = asset_performance[index] || {};
                      return (
                        <TableRow
                          key={monthName}
                          hover
                          sx={{
                            '&:hover': { bgcolor: isDark ? 'rgba(178, 140, 80, 0.25)' : 'rgba(178, 140, 80, 0.15)' },
                            bgcolor: index % 2 === 0 ? (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(166, 161, 155, 0.2)') : 'transparent'
                          }}
                        >
                          <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 'medium', color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>
                            {monthName}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>
                            {formatEnergy(asset.load_kwh)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>
                            {formatEnergy(asset.grid_to_load_kwh)}
                          </TableCell>
                          {hasSolar && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>
                              {formatEnergy(asset.pv_production_kwh || asset.pv_to_load_kwh)}
                            </TableCell>
                          )}
                          {hasSolar && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>
                              {formatEnergy(asset.pv_to_grid_kwh || 0)} / {formatEnergy(asset.storage_to_grid_kwh || 0)}
                            </TableCell>
                          )}
                          {hasStorage && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>
                              {formatEnergy(asset.storage_to_load_kwh)}
                            </TableCell>
                          )}
                          {hasStorage && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>
                              {asset.battery_cycles?.toFixed(1) || '-'}
                            </TableCell>
                          )}
                          {hasGenerator && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>
                              {formatEnergy(asset.generator_to_load_kwh)}
                            </TableCell>
                          )}
                          {hasGenerator && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>
                              {asset.generator_runtime_hours?.toFixed(0) || '-'}
                            </TableCell>
                          )}
                          {hasGenerator && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>
                              {asset.generator_fuel_used_mmbtu?.toFixed(2) || '-'}
                            </TableCell>
                          )}
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>
                            {formatPower(asset.peak_load_kw)} / {formatPower(asset.post_der_peak_kw || asset.grid_peak_kw)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Annual Summary Row */}
                    {asset_performance.length > 0 && (
                      <TableRow
                        sx={{
                          bgcolor: isDark ? 'rgba(178, 140, 80, 0.3)' : 'rgba(178, 140, 80, 0.25)',
                          '& td': { fontWeight: 700 }
                        }}
                      >
                        <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 700, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>
                          Annual
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.85rem' }}>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>SUM</Typography>
                          {formatEnergy(asset_performance.reduce((sum, m) => sum + (m.load_kwh || 0), 0))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.85rem' }}>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>SUM</Typography>
                          {formatEnergy(asset_performance.reduce((sum, m) => sum + (m.grid_to_load_kwh || 0), 0))}
                        </TableCell>
                        {hasSolar && (
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.85rem' }}>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>SUM</Typography>
                            {formatEnergy(asset_performance.reduce((sum, m) => sum + (m.pv_production_kwh || m.pv_to_load_kwh || 0), 0))}
                          </TableCell>
                        )}
                        {hasSolar && (
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.85rem' }}>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>SUM</Typography>
                            {formatEnergy(asset_performance.reduce((sum, m) => sum + (m.pv_to_grid_kwh || 0), 0))} / {formatEnergy(asset_performance.reduce((sum, m) => sum + (m.storage_to_grid_kwh || 0), 0))}
                          </TableCell>
                        )}
                        {hasStorage && (
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.85rem' }}>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>SUM</Typography>
                            {formatEnergy(asset_performance.reduce((sum, m) => sum + (m.storage_to_load_kwh || 0), 0))}
                          </TableCell>
                        )}
                        {hasStorage && (
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.85rem' }}>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>SUM</Typography>
                            {asset_performance.reduce((sum, m) => sum + (m.battery_cycles || 0), 0).toFixed(1)}
                          </TableCell>
                        )}
                        {hasGenerator && (
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.85rem' }}>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>SUM</Typography>
                            {formatEnergy(asset_performance.reduce((sum, m) => sum + (m.generator_to_load_kwh || 0), 0))}
                          </TableCell>
                        )}
                        {hasGenerator && (
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.85rem' }}>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>SUM</Typography>
                            {asset_performance.reduce((sum, m) => sum + (m.generator_runtime_hours || 0), 0).toFixed(0)}
                          </TableCell>
                        )}
                        {hasGenerator && (
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.85rem' }}>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>SUM</Typography>
                            {asset_performance.reduce((sum, m) => sum + (m.generator_fuel_used_mmbtu || 0), 0).toFixed(2)}
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.85rem' }}>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>AVG</Typography>
                          {formatPower(asset_performance.reduce((sum, m) => sum + (m.peak_load_kw || 0), 0) / 12)} / {formatPower(asset_performance.reduce((sum, m) => sum + (m.post_der_peak_kw || m.grid_peak_kw || 0), 0) / 12)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Savings Tab */}
            {activeTab === 1 && (
              <>
                {/* Sub-tabs for Utility, DER Charges, Summary */}
                {!utilityCostsOnly && (
                <Tabs
                  value={savingsSubTab}
                  onChange={(e, newValue) => setSavingsSubTab(newValue)}
                  sx={{
                    mb: 2,
                    minHeight: 36,
                    '& .MuiTab-root': {
                      fontFamily: vortxTypography.body,
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      minHeight: 36,
                      py: 0.5,
                      color: 'text.secondary',
                      '&.Mui-selected': {
                        color: vortxColors.gold,
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: vortxColors.gold,
                    }
                  }}
                >
                  <Tab label="Utility Costs" />
                  <Tab label="CA DER Charges" />
                  <Tab label="Summary" />
                </Tabs>
                )}

                {/* Sub-tab 0: Utility & Fuel Costs - BAU vs Project comparison */}
                {savingsSubTab === 0 && (
                  <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', border: `1px solid rgba(63, 106, 100, 0.2)` }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 60, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>Month</TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: 'text.secondary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.8rem' }}>
                            BAU Energy<br /><Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>Cost</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: 'text.secondary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.8rem' }}>
                            BAU Demand<br /><Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>Cost</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>
                            BAU Total
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: 'text.secondary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.8rem' }}>
                            Project Energy<br /><Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>Cost</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: 'text.secondary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.8rem' }}>
                            Project Demand<br /><Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>Cost</Typography>
                          </TableCell>
                          {hasGenerator && hasFuelCostBreakdown && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: 'text.secondary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.8rem' }}>
                              Fuel Delivery<br /><Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>Gas</Typography>
                            </TableCell>
                          )}
                          {hasGenerator && hasFuelCostBreakdown && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: 'text.secondary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.8rem' }}>
                              Fuel Commodity<br /><Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>Gas</Typography>
                            </TableCell>
                          )}
                          {hasGenerator && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: 'text.secondary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '0.8rem' }}>
                              {hasFuelCostBreakdown ? 'Fuel Total' : 'Fuel Cost'}<br /><Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>Generator</Typography>
                            </TableCell>
                          )}
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>
                            Project Total
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: '#4CAF50', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>
                            Utility Savings
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {MONTH_NAMES.map((monthName, index) => {
                          const bau = bau_monthly_costs[index] || {};
                          const project = project_monthly_costs[index] || {};
                          const asset = asset_performance[index] || {};
                          const bauTotal = (bau.energy_cost || 0) + (bau.demand_cost || 0);
                          const fuelCost = asset.generator_fuel_cost || 0;
                          const fuelDeliveryCost = asset.generator_fuel_delivery_cost || 0;
                          const fuelCommodityCost = asset.generator_fuel_commodity_cost || 0;
                          const projectTotal = (project.energy_cost || 0) + (project.demand_cost || 0) + fuelCost;
                          const utilitySavings = bauTotal - projectTotal;
                          return (
                            <TableRow key={monthName} hover sx={{ '&:hover': { bgcolor: isDark ? 'rgba(178, 140, 80, 0.25)' : 'rgba(178, 140, 80, 0.15)' }, bgcolor: index % 2 === 0 ? (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(166, 161, 155, 0.2)') : 'transparent' }}>
                              <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 'medium', color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{monthName}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>{formatCurrency(bau.energy_cost)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>{formatCurrency(bau.demand_cost)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 600, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{formatCurrency(bauTotal)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>{formatCurrency(project.energy_cost)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>{formatCurrency(project.demand_cost)}</TableCell>
                              {hasGenerator && hasFuelCostBreakdown && <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>{formatCurrency(fuelDeliveryCost)}</TableCell>}
                              {hasGenerator && hasFuelCostBreakdown && <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>{formatCurrency(fuelCommodityCost)}</TableCell>}
                              {hasGenerator && <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75, fontSize: '0.85rem' }}>{formatCurrency(fuelCost)}</TableCell>}
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 600, color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{formatCurrency(projectTotal)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 'bold', color: utilitySavings > 0 ? '#4CAF50' : '#f44336', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{formatCurrency(utilitySavings)}</TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Annual row */}
                        <TableRow sx={{ bgcolor: isDark ? 'rgba(178, 140, 80, 0.3)' : 'rgba(178, 140, 80, 0.25)', '& td': { fontWeight: 700 } }}>
                          <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 700, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>Annual</TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(bau_monthly_costs.reduce((sum, m) => sum + (m.energy_cost || 0), 0))}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(bau_monthly_costs.reduce((sum, m) => sum + (m.demand_cost || 0), 0))}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(bau_monthly_costs.reduce((sum, m) => sum + (m.energy_cost || 0) + (m.demand_cost || 0), 0))}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(project_monthly_costs.reduce((sum, m) => sum + (m.energy_cost || 0), 0))}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(project_monthly_costs.reduce((sum, m) => sum + (m.demand_cost || 0), 0))}</TableCell>
                          {hasGenerator && hasFuelCostBreakdown && <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(asset_performance.reduce((sum, m) => sum + (m.generator_fuel_delivery_cost || 0), 0))}</TableCell>}
                          {hasGenerator && hasFuelCostBreakdown && <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(asset_performance.reduce((sum, m) => sum + (m.generator_fuel_commodity_cost || 0), 0))}</TableCell>}
                          {hasGenerator && <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.secondary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(asset_performance.reduce((sum, m) => sum + (m.generator_fuel_cost || 0), 0))}</TableCell>}
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(project_monthly_costs.reduce((sum, m) => sum + (m.energy_cost || 0) + (m.demand_cost || 0), 0) + asset_performance.reduce((sum, m) => sum + (m.generator_fuel_cost || 0), 0))}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 'bold', color: '#4CAF50', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(bau_monthly_costs.reduce((sum, m) => sum + (m.energy_cost || 0) + (m.demand_cost || 0), 0) - project_monthly_costs.reduce((sum, m) => sum + (m.energy_cost || 0) + (m.demand_cost || 0), 0) - asset_performance.reduce((sum, m) => sum + (m.generator_fuel_cost || 0), 0))}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Sub-tab 1: CA DER Charges - Departing Load and Standby only */}
                {savingsSubTab === 1 && (
                  <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', border: `1px solid rgba(63, 106, 100, 0.2)` }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 60, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>Month</TableCell>
                          {hasDepartingLoadCharges && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: '#ff9800', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>
                              Departing Load<br /><Typography variant="caption" sx={{ color: '#ff9800', fontSize: '0.7rem' }}>PCIA + NBCs</Typography>
                            </TableCell>
                          )}
                          {hasStandbyCharges && (
                            <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: '#ff9800', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>
                              Standby<br /><Typography variant="caption" sx={{ color: '#ff9800', fontSize: '0.7rem' }}>Reservation</Typography>
                            </TableCell>
                          )}
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: '#f44336', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>
                            Total CA DER
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {MONTH_NAMES.map((monthName, index) => {
                          const derCharges = monthly_der_charges[index] || {};
                          const departingLoadCost = derCharges.departing_load_cost || 0;
                          const standbyCost = derCharges.standby_cost || 0;
                          const totalDerCost = departingLoadCost + standbyCost;
                          return (
                            <TableRow key={monthName} hover sx={{ '&:hover': { bgcolor: isDark ? 'rgba(178, 140, 80, 0.25)' : 'rgba(178, 140, 80, 0.15)' }, bgcolor: index % 2 === 0 ? (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(166, 161, 155, 0.2)') : 'transparent' }}>
                              <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 'medium', color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{monthName}</TableCell>
                              {hasDepartingLoadCharges && <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: '#ff9800', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{formatCurrency(departingLoadCost)}</TableCell>}
                              {hasStandbyCharges && <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: '#ff9800', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{formatCurrency(standbyCost)}</TableCell>}
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 'bold', color: '#f44336', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{formatCurrency(totalDerCost)}</TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Annual row */}
                        <TableRow sx={{ bgcolor: isDark ? 'rgba(178, 140, 80, 0.3)' : 'rgba(178, 140, 80, 0.25)', '& td': { fontWeight: 700 } }}>
                          <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 700, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>Annual</TableCell>
                          {hasDepartingLoadCharges && <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: '#ff9800', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(monthly_der_charges.reduce((sum, m) => sum + (m.departing_load_cost || 0), 0))}</TableCell>}
                          {hasStandbyCharges && <TableCell align="right" sx={{ fontFamily: vortxTypography.body, color: '#ff9800', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(monthly_der_charges.reduce((sum, m) => sum + (m.standby_cost || 0), 0))}</TableCell>}
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 'bold', color: '#f44336', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(monthly_der_charges.reduce((sum, m) => sum + (m.departing_load_cost || 0) + (m.standby_cost || 0), 0))}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Sub-tab 2: Summary - Net Savings */}
                {savingsSubTab === 2 && (
                  <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', border: `1px solid rgba(63, 106, 100, 0.2)` }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 700, width: 60, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>Month</TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: '#4CAF50', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>
                            Utility Savings<br /><Typography variant="caption" sx={{ color: '#81C784', fontSize: '0.7rem' }}>BAU - Project - Fuel</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: '#f44336', borderBottom: `2px solid ${vortxColors.gold}`, py: 1 }}>
                            CA DER Charges<br /><Typography variant="caption" sx={{ color: '#ef9a9a', fontSize: '0.7rem' }}>DL + Standby</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 700, bgcolor: 'background.paper', color: vortxColors.gold, borderBottom: `2px solid ${vortxColors.gold}`, py: 1, fontSize: '1rem' }}>
                            Net Savings
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {MONTH_NAMES.map((monthName, index) => {
                          const bau = bau_monthly_costs[index] || {};
                          const project = project_monthly_costs[index] || {};
                          const asset = asset_performance[index] || {};
                          const derCharges = monthly_der_charges[index] || {};
                          const fuelCost = asset.generator_fuel_cost || 0;
                          const utilitySavings = ((bau.energy_cost || 0) + (bau.demand_cost || 0)) - ((project.energy_cost || 0) + (project.demand_cost || 0)) - fuelCost;
                          const totalDerCost = (derCharges.departing_load_cost || 0) + (derCharges.standby_cost || 0);
                          const netSavings = utilitySavings - totalDerCost;
                          return (
                            <TableRow key={monthName} hover sx={{ '&:hover': { bgcolor: isDark ? 'rgba(178, 140, 80, 0.25)' : 'rgba(178, 140, 80, 0.15)' }, bgcolor: index % 2 === 0 ? (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(166, 161, 155, 0.2)') : 'transparent' }}>
                              <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 'medium', color: 'text.primary', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{monthName}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 600, color: utilitySavings > 0 ? '#4CAF50' : '#f44336', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{formatCurrency(utilitySavings)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 600, color: '#f44336', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>({formatCurrency(totalDerCost)})</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 'bold', fontSize: '1rem', color: netSavings > 0 ? '#4CAF50' : '#f44336', borderBottom: `1px solid rgba(178, 140, 80, 0.3)`, py: 0.75 }}>{formatCurrency(netSavings)}</TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Annual row */}
                        {(() => {
                          const totalFuelCost = asset_performance.reduce((sum, m) => sum + (m.generator_fuel_cost || 0), 0);
                          const totalUtilitySavings = bau_monthly_costs.reduce((sum, m) => sum + (m.energy_cost || 0) + (m.demand_cost || 0), 0) - project_monthly_costs.reduce((sum, m) => sum + (m.energy_cost || 0) + (m.demand_cost || 0), 0) - totalFuelCost;
                          const totalDerCost = monthly_der_charges.reduce((sum, m) => sum + (m.departing_load_cost || 0) + (m.standby_cost || 0), 0);
                          const totalNetSavings = totalUtilitySavings - totalDerCost;
                          return (
                            <TableRow sx={{ bgcolor: isDark ? 'rgba(178, 140, 80, 0.3)' : 'rgba(178, 140, 80, 0.25)', '& td': { fontWeight: 700 } }}>
                              <TableCell sx={{ fontFamily: vortxTypography.body, fontWeight: 700, color: 'text.primary', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>Annual</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 'bold', color: '#4CAF50', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(totalUtilitySavings)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 'bold', color: '#f44336', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>({formatCurrency(totalDerCost)})</TableCell>
                              <TableCell align="right" sx={{ fontFamily: vortxTypography.body, fontWeight: 'bold', fontSize: '1.1rem', color: totalNetSavings > 0 ? '#4CAF50' : '#f44336', borderTop: `2px solid ${vortxColors.gold}`, py: 1 }}>{formatCurrency(totalNetSavings)}</TableCell>
                            </TableRow>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Tariff Summary Card */}
                {(() => {
                  const resultsTariff = results?.inputs?.ElectricTariff;
                  const outputsTariff = results?.outputs?.ElectricTariff;
                  const metadata = resultsTariff?.urdb_metadata;

                  // Tariff name - use rate_name from metadata (e.g., "TOU-8, Option D")
                  const tariffName = metadata?.rate_name ||
                    selectedTariff?.name ||
                    selectedTariff?.label ||
                    resultsTariff?.urdb_rate_name ||
                    'Rate structure used';

                  // Calculate avg energy rate from outputs average series
                  const outputs = results?.outputs;
                  let avgEnergyRate = null;

                  if (outputsTariff?.energy_rate_average_series?.length > 0) {
                    const rates = outputsTariff.energy_rate_average_series;
                    avgEnergyRate = rates.reduce((sum, r) => sum + r, 0) / rates.length;
                  } else if (resultsTariff?.blended_annual_energy_rate) {
                    avgEnergyRate = resultsTariff.blended_annual_energy_rate;
                  } else if (selectedTariff?.blended_rate) {
                    avgEnergyRate = selectedTariff.blended_rate;
                  }

                  // If no valid energy rate found, throw error - don't use fallback
                  if (!avgEnergyRate || avgEnergyRate <= 0) {
                    console.error('No valid energy rate found in results', {
                      outputsTariff,
                      resultsTariff,
                      selectedTariff
                    });
                    throw new Error('No valid tariff energy rate found. Please ensure a tariff is selected before running optimization.');
                  }

                  // Calculate avg demand charge — check TOU demand rates, then facility demand rates, then blended
                  let avgDemandCharge = 0;
                  if (outputsTariff?.demand_rate_average_series?.length > 0) {
                    // TOU demand rates (timeseries average)
                    const demandRates = outputsTariff.demand_rate_average_series.filter(r => r > 0);
                    if (demandRates.length > 0) {
                      avgDemandCharge = demandRates.reduce((sum, r) => sum + r, 0) / demandRates.length;
                    }
                  }
                  if (avgDemandCharge === 0 && outputsTariff?.facility_demand_monthly_rate_series) {
                    // Facility demand rates — Dict keyed by tier (e.g. "Tier_1": [12 monthly $/kW rates])
                    // Sum across tiers, then average across months with non-zero rates
                    const tierRates = outputsTariff.facility_demand_monthly_rate_series;
                    const monthlyTotals = new Array(12).fill(0);
                    for (const tier of Object.values(tierRates)) {
                      if (Array.isArray(tier)) {
                        tier.forEach((rate, i) => { monthlyTotals[i] += rate; });
                      }
                    }
                    const nonZero = monthlyTotals.filter(r => r > 0);
                    if (nonZero.length > 0) {
                      avgDemandCharge = nonZero.reduce((sum, r) => sum + r, 0) / nonZero.length;
                    }
                  }
                  if (avgDemandCharge === 0 && resultsTariff?.blended_annual_demand_rate) {
                    avgDemandCharge = resultsTariff.blended_annual_demand_rate;
                  } else if (avgDemandCharge === 0 && selectedTariff?.blended_annual_demand_rate) {
                    avgDemandCharge = selectedTariff.blended_annual_demand_rate;
                  }

                  const fixedCharge = resultsTariff?.annual_min_charge
                    ? resultsTariff.annual_min_charge / 12
                    : (selectedTariff?.fixedmonthlycharge || 0);

                  return (
                    <Box sx={{ mt: 3, p: 2, bgcolor: isDark ? 'rgba(63, 106, 100, 0.2)' : 'rgba(63, 106, 100, 0.1)', borderRadius: 2, border: `1px solid ${vortxColors.green}` }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontFamily: vortxTypography.heading, color: 'text.primary', fontWeight: 600 }}>
                        Tariff Summary
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontFamily: vortxTypography.body, color: 'text.primary' }}>
                          <strong>Tariff:</strong> {tariffName}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: vortxTypography.body, color: 'text.primary' }}>
                          <strong>Avg. Energy Rate:</strong> ${avgEnergyRate.toFixed(4)}/kWh
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: vortxTypography.body, color: 'text.primary' }}>
                          <strong>Avg. Demand Charge:</strong> ${avgDemandCharge.toFixed(2)}/kW
                        </Typography>
                        {fixedCharge > 0 && (
                          <Typography variant="body2" sx={{ fontFamily: vortxTypography.body, color: 'text.primary' }}>
                            <strong>Fixed Charge:</strong> ${fixedCharge.toFixed(2)}/month
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })()}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}